import * as THREE from "three";
//#region ../src/visualization/text-sprite.ts
/**
* Shared CanvasTexture→SpriteMaterial→Sprite text-label helper.
*
* Single implementation behind the two in-scene text consumers:
* - gps-compass-cubes: static one-shot glyph labels (defaults),
* - wayfinding-hud: dynamic distance labels redrawn per change
*   (pill background, linear filters, explicit renderOrder).
*
* Text always renders with a change-detection guard: setText() with the
* current text is a no-op, so per-frame callers never pay a canvas redraw
* or a GPU texture upload for unchanged labels.
*/
const DEFAULT_TEXT_SPRITE_OPTIONS = {
	text: "",
	canvasWidth: 64,
	canvasHeight: 64,
	font: "bold 48px sans-serif",
	textColor: "#ffffff",
	background: "none",
	linearFilters: false,
	renderOrder: 0
};
/** Inner padding and corner radius of the 'pill' background, in canvas px. */
const PILL_PADDING_PX = 10;
const PILL_RADIUS_PX = 30;
/**
* Paint the optional pill background. roundRect is a relatively recent
* 2D-context API — fall back to a square-cornered rect on engines that
* lack it rather than crashing.
*/
function drawPill(ctx, canvasWidth, canvasHeight) {
	const w = canvasWidth - 2 * PILL_PADDING_PX;
	const h = canvasHeight - 2 * PILL_PADDING_PX;
	ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
	ctx.beginPath();
	if (typeof ctx.roundRect === "function") ctx.roundRect(PILL_PADDING_PX, PILL_PADDING_PX, w, h, PILL_RADIUS_PX);
	else ctx.rect(PILL_PADDING_PX, PILL_PADDING_PX, w, h);
	ctx.fill();
}
/** Clear the canvas and draw the label text (plus optional background). */
function drawLabel(ctx, opts, text) {
	const { canvasWidth, canvasHeight } = opts;
	ctx.clearRect(0, 0, canvasWidth, canvasHeight);
	if (opts.background === "pill") drawPill(ctx, canvasWidth, canvasHeight);
	ctx.font = opts.font;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.fillStyle = opts.textColor;
	ctx.fillText(text, canvasWidth / 2, canvasHeight / 2);
}
function buildMaterial(texture, opts) {
	const params = {
		map: texture,
		depthTest: false
	};
	if (opts.depthWrite !== void 0) params.depthWrite = opts.depthWrite;
	if (opts.transparent !== void 0) params.transparent = opts.transparent;
	return new THREE.SpriteMaterial(params);
}
/**
* Create a text sprite backed by an off-screen canvas texture.
*
* Tolerates a null 2D context (headless test environments without a canvas
* backend): the sprite is still created and setText()/dispose() stay safe;
* only the pixels are skipped.
*/
function createTextSprite(options = {}) {
	const opts = {
		...DEFAULT_TEXT_SPRITE_OPTIONS,
		...options
	};
	const canvas = document.createElement("canvas");
	canvas.width = opts.canvasWidth;
	canvas.height = opts.canvasHeight;
	const ctx = canvas.getContext("2d");
	const texture = new THREE.CanvasTexture(canvas);
	if (opts.linearFilters) {
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
	}
	const sprite = new THREE.Sprite(buildMaterial(texture, opts));
	sprite.renderOrder = opts.renderOrder;
	if (opts.scale) sprite.scale.set(opts.scale.x, opts.scale.y, opts.scale.z);
	let currentText = null;
	function setText(newText) {
		if (newText === currentText) return;
		currentText = newText;
		if (!ctx) return;
		drawLabel(ctx, opts, newText);
		texture.needsUpdate = true;
	}
	setText(opts.text);
	return {
		sprite,
		setText,
		dispose() {
			sprite.material.dispose();
			texture.dispose();
		}
	};
}
//#endregion
export { createTextSprite };
